import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 2 * 60 * 1000;

/** Polls /api/meta/version and signals when the server is serving a build
 *  different from the one THIS tab loaded with.
 *
 *  The server reports its build as a git SHORT_SHA (`APP_VERSION=${SHORT_SHA}`,
 *  set by Cloud Build), and the web bundle has no matching SHA baked in —
 *  only the semver `__APP_VERSION__`, a different identifier that would
 *  never equal the server's SHA. So the baseline cannot come from the
 *  bundle: it's captured from the FIRST version poll (the SHA the server
 *  was serving the instant this tab loaded), and every later poll is
 *  compared against it.
 *
 *  We run our OWN dedicated poll rather than reusing `useConfig()` — that's
 *  a shared React Query cache that refetches on tab focus, so reading it as
 *  the baseline let the baseline drift to the new SHA after a deploy; the
 *  poll then compared the new version against itself and the banner never
 *  fired. A ref captured once here can't drift; only a full page reload
 *  (which loads the new bundle) resets it. */
export function useVersionUpdate() {
  const baselineRef = useRef<string | null>(null);
  const [remoteVersion, setRemoteVersion] = useState<string | null>(null);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await api.meta.version();
        if (cancelled || !res.version) return;
        // First successful poll defines the baseline (the build this tab
        // loaded with). Set before the state update so the comparison
        // below always has a baseline to read.
        if (baselineRef.current === null) baselineRef.current = res.version;
        setRemoteVersion(res.version);
      } catch {
        /* silent — retry on the next tick */
      }
    };

    void check(); // capture the baseline immediately on mount
    const interval = setInterval(check, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const updateAvailable = Boolean(
    remoteVersion &&
      baselineRef.current &&
      remoteVersion !== baselineRef.current &&
      remoteVersion !== dismissedVersion,
  );

  return {
    updateAvailable,
    /** The newer build the server is advertising (display/telemetry). */
    remoteVersion,
    refresh: refreshWithCacheClear,
    /** Suppress the banner for the current remote build; a later deploy
     *  (a different SHA) re-raises it. */
    dismiss: () => setDismissedVersion(remoteVersion),
  };
}

/** Hard-reload that also clears any Cache Storage entries the browser
 *  might have stashed. We don't currently register a service worker, so
 *  there shouldn't be anything in there — but a PWA install or browser
 *  extension can populate Cache Storage anyway, and stale entries
 *  there would survive a normal reload. Best-effort; falls back to a
 *  plain reload on any error. */
async function refreshWithCacheClear() {
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore — clearing Cache Storage is best-effort */
  }
  // Forced reload (location.reload(true) is deprecated; modern browsers
  // already ignore in-memory caches when explicitly reloading). The new
  // index.html will reference the new hashed chunks so the rest of the
  // bundle naturally picks up the latest build.
  window.location.reload();
}
